import React, { useEffect, useMemo, useRef } from 'react';
import { MarketNftStatuses, NftUpdate } from '../Interfaces';
import useAlert from '../../../../contexts/alert';
import useInitFight from '../../../../contexts/initFight';
import useMint from '../../../../hooks/useMint';
import Button from '../../../controls/button';
import { API_NftDetails, MintTxStatuses } from 'shared';
import fetcher from '../../../../utils/fetcher';
import { SetState } from '../../../../interfaces/SetState';
import localStorage from '../../../../utils/localStorage';
import { useNavigate, useSearchParams } from 'react-router-dom';
import usePromo, { calculateNewPrice } from '../promoCode/Context';

interface Props {
  nft: API_NftDetails;
  status: MarketNftStatuses;
  inactive: boolean;
  update: NftUpdate;
  className?: string;
  loading: boolean;
  setLoading: SetState<boolean>;
}

const MarketPageActionButton = ({
  nft,
  status,
  inactive,
  className,
  update,
  loading,
  setLoading
}: Props) => {
  const [params] = useSearchParams();
  const urlParsed = useRef(false);
  const { appliedCode, apply } = usePromo();
  const alert = useAlert();
  const navigate = useNavigate();
  const { openPickDialog } = useInitFight();
  const { mint, secretMint } = useMint();

  useEffect(() => {
    if (!urlParsed.current) {
      urlParsed.current = true;
      const token = params.get('id');
      const shouldMint = params.get('mint');
      const promo = params.get('code');
      if (token === nft.token && shouldMint === '1') {
        setLoading(true);
        if (promo) {
          apply(promo, (result) => {
            if (result) {
              handleMint(result, nft);
            } else {
              setLoading(false);
              alert.showError('Invalid code');
            }
          });
        } else {
          handleMint(undefined, nft);
        }
      }
    }
  }, [params, nft]);

  const buttonProps = useMemo(() => {
    const getTitle = (status: MarketNftStatuses): string => {
      switch (status) {
        case MarketNftStatuses.UNAVAILABLE:
          return 'Minted By Somebody Else';
        case MarketNftStatuses.MINTED:
          return 'Go Fight';
        case MarketNftStatuses.FAILED:
          return 'Mint again';
        case MarketNftStatuses.PENDING:
          return 'Update Status';
        default: //available
          return 'Mint';
      }
    };

    return {
      title: getTitle(status),
      disabled: status === MarketNftStatuses.UNAVAILABLE
    };
  }, [status]);

  const handleClick = () => {
    switch (status) {
      case MarketNftStatuses.UNAVAILABLE:
        return;
      case MarketNftStatuses.MINTED:
        return handleFight();
      case MarketNftStatuses.PENDING:
        return handleUpdate();
      default: //failed, available
        return handleMint(appliedCode, nft);
    }
  };

  const handleFight = () => {
    setLoading(true);
    openPickDialog(nft, () => setLoading(false));
  };

  const handleUpdate = () => {
    setLoading(true);
    fetcher.nft
      .updateStatus(nft.token)
      .then((response) => {
        if (
          'needUpdate' in response ||
          response.status !== MintTxStatuses.PENDING
        ) {
          return update(() => setLoading(false));
        }
        alert.show('Nothing has changed so far');
        setLoading(false);
      })
      .catch((err: any) => {
        alert.showError(err);
        setLoading(false);
      });
  };

  const handleMint = (code: typeof appliedCode, nft: API_NftDetails) => {
    const onMint = (token) => {
      alert.show('Transaction is initiated');
      localStorage.setPrefNftToken(token);
      setTimeout(() => {
        setLoading(false);
        navigate('/');
      }, 500);
    };
    if (code) {
      const newPrice = calculateNewPrice(nft.price, code.discount);
      secretMint(setLoading, nft.token, newPrice, code.code, onMint);
    } else {
      mint(setLoading, nft.token, nft.price, onMint);
    }
  };

  return (
    <Button
      className={className}
      loading={loading}
      disabled={buttonProps.disabled}
      onClick={inactive ? undefined : handleClick}
    >
      {buttonProps.title}
    </Button>
  );
};

export default MarketPageActionButton;
